/**
 * Pre-analyze validation for the standalone file-import flow. Pure.
 */
import { DATA_SOURCE_DETAIL_MAX_LENGTH } from '@shared/schemas';

import { type DataSourceDraft, showsDetailField, showsToolPicker } from './data-source';
import type { FileImportError } from './file-import-error';

export const IMPORT_NAME_REQUIRED_MESSAGE = 'Please enter an import name';
export const DATA_SOURCE_REQUIRED_MESSAGE = 'Please choose a data source';
export const DATA_SOURCE_TOOL_REQUIRED_MESSAGE = 'Please choose the tool the data comes from';
export const DATA_SOURCE_DETAIL_REQUIRED_MESSAGE = 'Please describe the data source';
export const DATA_SOURCE_DETAIL_TOO_LONG_MESSAGE = `The data source must be at most ${DATA_SOURCE_DETAIL_MAX_LENGTH} characters`;
/** Shown in the dropzone while the client import's data source is incomplete. */
export const DATA_SOURCE_FIRST_HINT = 'Choose a data source first';

/**
 * The data source's first missing piece (#749), keyed by the form field that
 * shows it: the category, then the tool, then the free text (trimmed, at most
 * 100 characters).
 */
export function dataSourceErrors(draft: DataSourceDraft): Record<string, string> {
  if (!draft.category) return { dataSourceCategory: DATA_SOURCE_REQUIRED_MESSAGE };
  if (showsToolPicker(draft) && !draft.tool) return { dataSourceTool: DATA_SOURCE_TOOL_REQUIRED_MESSAGE };
  if (showsDetailField(draft)) {
    const detail = draft.detail.trim();
    if (!detail) return { dataSourceDetail: DATA_SOURCE_DETAIL_REQUIRED_MESSAGE };
    if (detail.length > DATA_SOURCE_DETAIL_MAX_LENGTH) {
      return { dataSourceDetail: DATA_SOURCE_DETAIL_TOO_LONG_MESSAGE };
    }
  }
  return {};
}

/**
 * Whether the client may drop a file (#749): the dropzone stays closed until
 * the data source is complete, so the flow never leaves Upload without one.
 * Admin mode (an existing list) has no data source.
 */
export function canUploadFile(input: { listId: string | null; dataSource: DataSourceDraft }): boolean {
  return input.listId !== null || Object.keys(dataSourceErrors(input.dataSource)).length === 0;
}

/**
 * Client mode (no listId) creates a list named after the import, so a
 * non-blank name is required before analyze can run, and so is the data
 * source (#749). Admin mode has nothing to validate. Returns null when the
 * flow may proceed.
 */
export function validateForAnalyze(input: {
  listId: string | null;
  importName: string;
  dataSource: DataSourceDraft;
}): FileImportError | null {
  if (input.listId) return null;
  const fields: Record<string, string> = {
    ...(input.importName.trim() ? {} : { importName: IMPORT_NAME_REQUIRED_MESSAGE }),
    ...dataSourceErrors(input.dataSource),
  };
  return Object.keys(fields).length > 0 ? { kind: 'validation', fields } : null;
}
